import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, PhoneOff, Video } from "lucide-react";
import { useCall } from "../context/CallContext";
import { resolveImg } from "../lib/api";

const IncomingCallModal = () => {
  const { incomingCall, setIncomingCall, sendSignal } = useCall() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const audioCtxRef = useRef(null);

  const ringing = !!incomingCall && !location.pathname.startsWith("/call");

  // Ringtone (Web Audio) + vibration while the call is ringing
  useEffect(() => {
    if (!ringing) return;
    const ring = () => {
      try {
        if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = audioCtxRef.current;
        if (ctx.state === "suspended") ctx.resume();
        const tone = (freq, start, dur) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
          g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.04);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
          o.connect(g);
          g.connect(ctx.destination);
          o.start(ctx.currentTime + start);
          o.stop(ctx.currentTime + start + dur + 0.05);
        };
        tone(880, 0, 0.4);
        tone(660, 0.5, 0.4);
      } catch (e) {}
    };
    const vibrate = () => {
      try { if (navigator.vibrate) navigator.vibrate([400, 200, 400]); } catch (e) {}
    };
    ring();
    vibrate();
    const iv = setInterval(() => { ring(); vibrate(); }, 2000);
    return () => {
      clearInterval(iv);
      try { if (navigator.vibrate) navigator.vibrate(0); } catch (e) {}
      try { audioCtxRef.current && audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    };
  }, [ringing]);

  if (!ringing) return null;

  const accept = () => {
    navigate(`/call/${incomingCall.match_id}`, { state: { role: "callee" } });
  };
  const decline = () => {
    sendSignal({ type: "reject", to: incomingCall.from });
    setIncomingCall(null);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-kora-ink/90 backdrop-blur-md flex flex-col items-center justify-center p-8" data-testid="incoming-call-modal">
      <div className="text-center">
        <div className="relative w-32 h-32 mx-auto">
          <img
            src={resolveImg(incomingCall.caller_photo)}
            alt={incomingCall.caller_name}
            className="w-32 h-32 rounded-full object-cover border-4 border-white/30"
          />
          <span className="absolute inset-0 rounded-full ring-4 ring-kora-terracotta/60 animate-ping" />
        </div>
        <div className="flex items-center justify-center gap-2 mt-6 text-white/80">
          <Video size={16} /> <span className="text-sm">Appel vidéo entrant</span>
        </div>
        <p className="font-display font-black text-3xl text-white mt-1" data-testid="incoming-caller-name">
          {incomingCall.caller_name || "Quelqu'un"}
        </p>
      </div>

      <div className="flex items-center gap-14 mt-14">
        <button data-testid="incoming-decline" onClick={decline} className="flex flex-col items-center gap-2">
          <span className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg">
            <PhoneOff size={28} />
          </span>
          <span className="text-white/70 text-xs">Refuser</span>
        </button>
        <button data-testid="incoming-accept" onClick={accept} className="flex flex-col items-center gap-2">
          <span className="w-16 h-16 rounded-full bg-kora-sage text-white flex items-center justify-center shadow-lg animate-bounce">
            <Phone size={28} />
          </span>
          <span className="text-white/70 text-xs">Répondre</span>
        </button>
      </div>
    </div>
  );
};

export default IncomingCallModal;
